import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { HEALTH_STATE } from "contracts/enums/core";
import { pathDepth } from "../../boundaries/url-path.parser";
import type { PageMapLens } from "../../enums/map";
import {
  type ClusterMetric,
  groupedPageCanvas,
  layeredClusterCanvas,
} from "./estateCanvasLayout";
import { clusterLabel, clusterSort } from "./estateFormat";
import { groupedPages } from "./estateRelationships";
import {
  CLUSTER_COLUMN_GAP,
  CLUSTER_HEADER_HEIGHT,
  CLUSTER_PADDING,
  CLUSTER_ROW_GAP,
  type EstateBand,
  type EstateLayout,
  type EstateNode,
  PAGE_NODE_HEIGHT,
  PAGE_NODE_WIDTH,
  PAGES_VIEWPORT,
  type Page,
  SUMMARY_NODE_HEIGHT,
  SUMMARY_NODE_WIDTH,
  SUMMARY_VIEWPORT,
} from "./estateTypes";
import { linkedIssueCount, scoreFor } from "./estateViewport";
import { isImportantPath } from "./issuePageResolution";
import { importantPagePaths, pageHealthClass } from "./scanModel";

export function layoutEstate(
  data: OverviewResponse,
  lens: PageMapLens,
  compact: boolean,
): EstateLayout {
  const viewport = compact ? SUMMARY_VIEWPORT : PAGES_VIEWPORT;
  const important = importantPagePaths(data);
  const groups = groupedPages(data.pages);
  const metrics = [...groups.keys()]
    .sort(clusterSort)
    .map((key) =>
      clusterMetric(key, groups.get(key) ?? [], lens, important, compact),
    );
  const canvas = compact
    ? layeredClusterCanvas(data, metrics, viewport.width)
    : groupedPageCanvas(metrics);
  const bands: EstateBand[] = [];
  const nodes: EstateNode[] = [];

  for (const placement of canvas.placements) {
    const { metric } = placement;
    const scores = metric.allPages
      .map((page) => scoreFor(page, lens))
      .filter((score): score is number => score !== null);
    const band: EstateBand = {
      key: metric.key,
      label: metric.label,
      x: placement.x,
      y: placement.y,
      width: metric.width,
      height: metric.height,
      pageCount: metric.allPages.length,
      representedPageCount: metric.visiblePages.length,
      averageScore:
        scores.length > 0
          ? Math.round(
              scores.reduce((total, score) => total + score, 0) / scores.length,
            )
          : null,
      weakPages: metric.allPages.filter((page) => {
        const score = scoreFor(page, lens);
        return score !== null && pageHealthClass(score) === HEALTH_STATE.POOR;
      }).length,
      importantPages: metric.allPages.filter((page) =>
        isImportantPath(important, page.path),
      ).length,
    };
    bands.push(band);
    const nodeWidth = compact ? SUMMARY_NODE_WIDTH : PAGE_NODE_WIDTH;
    const nodeHeight = compact ? SUMMARY_NODE_HEIGHT : PAGE_NODE_HEIGHT;

    metric.visiblePages.forEach((page, pageIndex) => {
      const column = pageIndex % metric.columns;
      const pageRow = Math.floor(pageIndex / metric.columns);
      const score = scoreFor(page, lens);
      nodes.push({
        page,
        x:
          band.x +
          CLUSTER_PADDING +
          nodeWidth / 2 +
          column * (nodeWidth + CLUSTER_COLUMN_GAP),
        y:
          band.y +
          CLUSTER_HEADER_HEIGHT +
          CLUSTER_PADDING +
          nodeHeight / 2 +
          pageRow * (nodeHeight + CLUSTER_ROW_GAP),
        width: nodeWidth,
        height: nodeHeight,
        cluster: metric.key,
        important: isImportantPath(important, page.path),
        issueCount: linkedIssueCount(data, page),
        score,
        state: pageHealthClass(score),
      });
    });
  }

  return {
    nodes,
    bands,
    width: canvas.width,
    height: canvas.height,
    viewport,
  };
}

function clusterMetric(
  key: string,
  pages: Page[],
  lens: PageMapLens,
  important: Set<string>,
  compact: boolean,
): ClusterMetric {
  const visiblePages = compact
    ? representativePages(pages, lens, important)
    : [...pages].sort(stablePageSort);
  const pageCount = Math.max(visiblePages.length, 1);
  const columns = compact
    ? Math.min(2, pageCount)
    : pageCount >= 13
      ? 4
      : pageCount >= 9
        ? 3
        : Math.min(2, pageCount);
  const rows = Math.max(1, Math.ceil(pageCount / columns));
  const nodeWidth = compact ? SUMMARY_NODE_WIDTH : PAGE_NODE_WIDTH;
  const nodeHeight = compact ? SUMMARY_NODE_HEIGHT : PAGE_NODE_HEIGHT;
  const label = clusterLabel(key, pages);
  const labelWidth = Math.min(34, Math.max(18, label.length * 1.05));
  return {
    key,
    label,
    allPages: pages,
    visiblePages,
    columns,
    rows,
    width: Math.max(
      labelWidth,
      CLUSTER_PADDING * 2 +
        columns * nodeWidth +
        Math.max(0, columns - 1) * CLUSTER_COLUMN_GAP,
    ),
    height:
      CLUSTER_HEADER_HEIGHT +
      CLUSTER_PADDING * 2 +
      rows * nodeHeight +
      Math.max(0, rows - 1) * CLUSTER_ROW_GAP,
  };
}

function representativePages(
  pages: Page[],
  lens: PageMapLens,
  important: Set<string>,
): Page[] {
  const selected = new Map<string, Page>();
  const add = (page: Page | undefined) => {
    if (page && selected.size < 3) {
      selected.set(page.id, page);
    }
  };
  const stable = [...pages].sort(stablePageSort);
  add(stable[0]);
  add(
    [...pages]
      .filter((page) => isImportantPath(important, page.path))
      .sort(scoreThenPath(lens))[0],
  );
  add(
    [...pages]
      .filter((page) => scoreFor(page, lens) !== null)
      .sort(scoreThenPath(lens))[0],
  );
  for (const page of stable) {
    add(page);
  }
  return [...selected.values()];
}

function scoreThenPath(lens: PageMapLens) {
  return (left: Page, right: Page) =>
    (scoreFor(left, lens) ?? 101) - (scoreFor(right, lens) ?? 101) ||
    stablePageSort(left, right);
}

function stablePageSort(left: Page, right: Page): number {
  return (
    pathDepth(left.path) - pathDepth(right.path) ||
    left.path.localeCompare(right.path)
  );
}
